import { h, createContext }                    from 'preact';
import { useContext, useState, useEffect }      from 'preact/compat';
import { useCookies }                           from '../../hooks';
import { INITIAL_APPLICATION_ALERTS_STATE, DEFAULT_STORE_TITLE } from './appContext.data';
import { readHandTalkScript, readGeneralApplicationDataScript, resolveServerUrl } from './appContext.utils';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const SERVER_URL = resolveServerUrl();

const INITIAL_RECAPTCHA = {
    isEnabled: null,
    token:     '',
};

const INITIAL_SOCIAL_LOGIN = {
    isGeneralEnabled:        false,
    isAccountLinkingEnabled: false,
    isGoogleEnabled:         false,
    isFacebookEnabled:       false,
};

const INITIAL_STORE_LOCATION = {
    id:   1,
    name: 'Brasília - DF',
};

const INITIAL_HAND_TALK = {
    enable: null,
    token:  '',
};

// ─────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────

const AppContext = createContext(null);

// TODO: move isLocationPickerOpen and isValidationModalOpen to BehavioralContext
export const AppContextProvider = ({ children }) => {

    // ── State ─────────────────────────────────

    const [locations,             setLocations]             = useState([]);
    const [isLocationPickerOpen,  setIsLocationPickerOpen]  = useState(false);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [recaptchaData,         setRecaptchaData]         = useState(INITIAL_RECAPTCHA);
    const [noticesData,           setNoticesData]           = useState([]);
    const [socialLoginConfig,     setSocialLoginConfig]     = useState(INITIAL_SOCIAL_LOGIN);
    const [generalApplicationData, setGeneralApplicationData] = useState({});
    const [fullPageLoader,   setFullPageLoader]   = useState(false);
    const [fullPageLoaderV2, setFullPageLoaderV2] = useState(false);
    const [applicationAlerts, setApplicationAlerts] = useState(INITIAL_APPLICATION_ALERTS_STATE);

    // ── Cookies ───────────────────────────────

    const [storeLocation,     setStoreLocation]     = useCookies('STORE_LOCATION', INITIAL_STORE_LOCATION);
    const [accessibilityFont, setAccessibilityFont] = useCookies('ACCESSIBILITY_FONT', 0);
    const [highContrast,      setHighContrast]      = useCookies('HIGH_CONTRAST', false);
    const [handTalkData,      setHandTalkData]      = useCookies('HAND_TALK', INITIAL_HAND_TALK);

    // ── Accessibility ─────────────────────────

    const handleAccessibilityFont = (fontSize) => {
        if (fontSize < 0 || fontSize > 2) return;
        setAccessibilityFont(fontSize);
    };

    const handleHighContrast = (state = undefined) => {
        setHighContrast(typeof state === 'boolean' ? state : !highContrast);
    };

    useEffect(() => {
        const html = document.documentElement;
        html.classList.toggle('dark',          highContrast);
        html.classList.toggle('high-contrast', highContrast);
    }, [highContrast]);

    // ── Document title ────────────────────────

    const handleDocumentTitle = (title = '') => {
        const defaultTitle =
            generalApplicationData.defaultStoreTitle || DEFAULT_STORE_TITLE;

        try {
            document.title = title ? `${title} | ${defaultTitle}` : defaultTitle;
        } catch {
            console.warn('Uncaught error while trying to update document title!');
            document.title = DEFAULT_STORE_TITLE;
        }
    };

    // ── Application alerts ────────────────────

    const handleApplicationAlerts = (type, content) => {
        if (!type || !content) return;

        setApplicationAlerts(prev => ({ ...prev, [type]: content }));
    };

    const resetApplicationAlerts = () => {
        setApplicationAlerts(INITIAL_APPLICATION_ALERTS_STATE);
    };

    // ── Setup effect ──────────────────────────

    useEffect(() => {
        const parsedHandTalk = readHandTalkScript();
        if (parsedHandTalk) setHandTalkData(parsedHandTalk);

        const parsedAppData = readGeneralApplicationDataScript();
        if (parsedAppData) setGeneralApplicationData(parsedAppData);
    }, []);

    // ── Route-change alert reset ──────────────

    useEffect(() => {
        const handleRouteChange = () => resetApplicationAlerts();

        window.addEventListener('popstate',    handleRouteChange);
        window.addEventListener('hashchange',  handleRouteChange);

        return () => {
            window.removeEventListener('popstate',   handleRouteChange);
            window.removeEventListener('hashchange', handleRouteChange);
        };
    }, []);

    // ─────────────────────────────────────────
    // Context value
    // ─────────────────────────────────────────

    const value = {
        // App data
        generalApplicationData,
        SERVER_URL,
        locations,
        setLocations,
        noticesData,
        setNoticesData,

        // Loaders
        fullPageLoader,
        setFullPageLoader,
        fullPageLoaderV2,
        setFullPageLoaderV2,

        // Location
        storeLocation,
        setStoreLocation,
        isLocationPickerOpen,
        setIsLocationPickerOpen,
        isValidationModalOpen,
        setIsValidationModalOpen,

        // Accessibility
        accessibilityFont,
        highContrast,
        handTalkData,
        handleAccessibilityFont,
        handleHighContrast,

        // Auth / integrations
        recaptchaData,
        setRecaptchaData,
        socialLoginConfig,
        setSocialLoginConfig,

        // Alerts
        applicationAlerts,
        handleApplicationAlerts,
        resetApplicationAlerts,

        // Document
        handleDocumentTitle,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

const useAppContext = () => useContext(AppContext);
export default useAppContext;
